import { CheckCircleOutlined, CopyOutlined } from '@ant-design/icons';
import { Button, theme, Tooltip } from 'antd';
import { type SizeType } from 'antd/es/config-provider/SizeContext';
import React, { useEffect, useRef, useState } from 'react';

function CopyToClipboard({
  text,
  title = 'Copy to clipboard!',
  children = null,
  size = 'large',
  type = 'default',
}: {
  text: string;
  title?: string;
  children?: React.ReactNode;
  size?: SizeType;
  type?: 'primary' | 'dashed' | 'link' | 'text' | 'default';
}): React.ReactElement {
  const { token } = theme.useToken();
  const [copied, setCopied] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  const childrenToShow: React.ReactNode = children || (
    <CopyOutlined style={{ color: token.colorPrimary }} />
  );

  if (!text) {
    return <CopyOutlined disabled />;
  }

  return (
    <Tooltip title={copied ? 'Copied!' : title}>
      <Button
        type={type}
        size={size}
        onClick={async (): Promise<void> => {
          await navigator.clipboard.writeText(text.trim());
          if (timerRef.current !== null) {
            clearTimeout(timerRef.current);
          }
          setCopied(true);
          timerRef.current = setTimeout((): void => setCopied(false), 3000);
        }}
      >
        {copied && !children ? (
          <CheckCircleOutlined style={{ color: token.colorSuccess }} />
        ) : (
          childrenToShow
        )}
      </Button>
    </Tooltip>
  );
}

export default CopyToClipboard;
